import { Button } from "@design-lab/shared/ui/design-system/Button";
import { type ChangeEvent, type KeyboardEvent, type RefObject } from "react";

type PinComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  canSubmit: boolean;
  isProcessing: boolean;
  isRemoving: boolean;
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  onNewline?: (cursorPosition: number) => void;
  compact?: boolean;
};

export function PinComposer({
  value,
  onChange,
  onSubmit,
  onCancel,
  canSubmit,
  isProcessing,
  isRemoving,
  textareaRef,
  onNewline,
  compact = false
}: PinComposerProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        const { selectionStart, selectionEnd } = e.currentTarget;
        const newValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`;
        onChange(newValue);
        onNewline?.(selectionStart + 1);
        return;
      }
      e.preventDefault();
      if (!isProcessing && canSubmit) {
        onSubmit();
      }
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  return (
    <>
      <div className={`flex ${compact ? "gap-1.5" : "gap-2"}`}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Add your instructions here..."
          className={`flex-1 resize-none rounded-md border border-gray-300 text-base outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 ${
            compact ? "min-h-[36px] px-2 py-1" : "min-h-[44px] px-3 py-2"
          }`}
          disabled={isProcessing}
          data-hj-allow
          data-testid="pinning-composer"
        />
      </div>
      <div className={`flex justify-end ${compact ? "mt-1 gap-1.5" : "mt-2 gap-2"}`}>
        <Button
          data-testid="pin-composer-cancel"
          variant="link"
          className={`font-medium text-gray-500 no-underline hover:text-gray-700 hover:no-underline ${
            compact ? "text-[11px]" : "text-xs"
          }`}
          onPress={onCancel}
          isDisabled={isRemoving}
        >
          {isRemoving ? "Removing..." : "Cancel"}
        </Button>
        <Button
          data-testid="pinning-send"
          className={`rounded bg-blue-600 font-bold text-white hover:bg-blue-700 disabled:opacity-50 ${
            compact ? "px-2.5 py-0.5 text-[11px]" : "px-4 py-1.5 text-xs"
          }`}
          isDisabled={isProcessing || !canSubmit}
          onPress={onSubmit}
        >
          Submit
        </Button>
      </div>
    </>
  );
}
